import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a stable callback reference that always calls the latest version of the provided function.
 *
 * This is useful when you need to pass a callback to a dependency array (like useEffect)
 * but don't want the effect to re-run when the callback's dependencies change.
 *
 * This is a polyfill for React's experimental `useEffectEvent` hook stable in react v19.
 *
 * @param fn - The callback function to stabilize
 * @returns A stable callback that always invokes the latest version of `fn`
 */
export function useEventCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef<T>(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });

  return useCallback(
    ((...args: Parameters<T>) => {
      return ref.current(...args);
    }) as T,
    [],
  );
}
